/**
 * What a slot holds.
 *
 * A tagged union rather than `unknown`, because the store has to answer two
 * questions about a value without opening it: is it populated (coverage), and
 * may it be written into this key (kind). Both are answered from the tag.
 */

import type { RoleKind } from '@morph/spec';
import type { ObjectId, AnchorId } from './ids.js';

export type KnownValue =
  | { readonly k: 'text';   readonly v: string }
  | { readonly k: 'number'; readonly v: number }
  | { readonly k: 'bool';   readonly v: boolean }
  | { readonly k: 'date';   readonly v: string }                              // ISO-8601
  | { readonly k: 'enum';   readonly v: string }
  | { readonly k: 'ref';    readonly v: ObjectId; readonly anchor?: AnchorId }
  | { readonly k: 'media';  readonly v: { readonly blob: string; readonly mime: string } }
  | { readonly k: 'list';   readonly v: readonly KnownValue[] };

/** Unknown future kinds round-trip and render via fallback (0005). The `x-`
 *  prefix keeps the union discriminable instead of degrading it to `string`. */
export type Value = KnownValue | { readonly k: `x-${string}`; readonly v: unknown };

/**
 * Which role kind a value can sit under.
 *
 * `bool` maps to `enum` because the closed vocabulary has no boolean kind and
 * should not grow one: a checkbox is a two-member enumeration, which is also
 * why `toggle` is legal against enum roles and nothing else.
 */
export function kindOf(v: Value): RoleKind | undefined {
  switch (v.k) {
    case 'text':   return 'text';
    case 'number': return 'number';
    case 'bool':   return 'enum';
    case 'date':   return 'date';
    case 'enum':   return 'enum';
    case 'ref':    return 'ref';
    case 'media':  return 'media';
    case 'list':   return v.v.length ? kindOf(v.v[0]!) : undefined;
    default:       return undefined;          // x-*: round-trips, binds to nothing
  }
}

/** Populated, in the same sense `generation/src/shape.ts` means it — an empty
 *  string is a hole in a column, not a value in it. Coverage counts holes, so
 *  the two definitions have to agree or the shape describes a different
 *  collection than the one on screen. */
export function isPopulated(v: Value): boolean {
  switch (v.k) {
    case 'text':
    case 'enum':
    case 'date':   return v.v.trim().length > 0;
    case 'number': return Number.isFinite(v.v);
    case 'bool':   return true;
    case 'ref':    return v.v.length > 0;
    case 'media':  return v.v.blob.length > 0;
    case 'list':   return v.v.some(isPopulated);
    default:       return v.v !== null && v.v !== undefined;
  }
}

/** A `score` role carries a number; every other kind must match exactly. An
 *  `x-` value fits nothing, so an unknown kind can be read back but never
 *  written into a typed slot. */
export function valueFitsKind(v: Value, kind: RoleKind): boolean {
  const k = kindOf(v);
  if (k === undefined) return false;
  if (kind === 'score') return k === 'number';
  // A recording is STORED like a still — a blob reference and a mime type — and
  // differs only in how it is presented. `av` is a presentation contract, not a
  // storage shape, which is the same relationship `score` already has to
  // `number` one line above. Without this, seeding a video refused with
  // `kind-mismatch` and the page rendered nothing at all.
  if (kind === 'av') return k === 'media';
  // And a document, for the identical reason. `doc` is a presentation
  // contract — pages, a scroll, a viewer that is not ours — over exactly the
  // storage shape a still and a recording already use: a blob reference and a
  // mime type. The comment above promised the relationship; this is the third
  // kind to rely on it, and each one has cost a refusal at the door before it
  // was added, because the store is right to refuse a shape it was not told
  // about.
  if (kind === 'doc') return k === 'media';
  return k === kind;
}

/**
 * The value as the renderer wants it: media resolves to its blob reference,
 * refs to the target id, lists to arrays.
 *
 * This is the one place a stored value loses its tag, and it happens on the way
 * *out* to a projection — never on the way into a shape, which sees counts only.
 */
export function rawOf(v: Value): unknown {
  switch (v.k) {
    case 'media': return v.v.blob;
    case 'list':  return v.v.map(rawOf);
    default:      return v.v;
  }
}
